import type { GroomingStage } from "@ttc-spectree/toolbox-contract";
import type { GroomingNextAction } from "@/lib/api/grooming";

/**
 * Plain words for the grooming pages (epic PI-SPEC-SW-E279). The pages are
 * read by a mixed room (product managers, testers, engineers), so every label
 * here avoids tracker and engineering jargon.
 */

/** Story Points on the scale the team uses. */
export const POINT_SCALE = [1, 2, 3, 5, 8, 13] as const;

export const STAGE_LABELS: Record<GroomingStage, string> = {
  "needs-information": "Needs information",
  "ready-to-groom": "Ready to groom",
  groomed: "Groomed",
};

/** Chip tone of each stage: warn, current (blue) or ok. */
export const STAGE_TONES: Record<GroomingStage, "warn" | "cur" | "ok"> = {
  "needs-information": "warn",
  "ready-to-groom": "cur",
  groomed: "ok",
};

export const NEXT_ACTION_LABELS: Record<GroomingNextAction, string> = {
  "ask-author": "Ask the author",
  "add-to-session": "Add to session",
  "review-brief": "Review brief",
  ready: "Ready for a sprint",
};

/**
 * A backlog row's next step (E292, UX-PM-11, frames D1 and D2): the API's
 * next action refined by the asked state. The first five are actions for a
 * person who can take them; the last two are always plain text.
 */
export type NextStep =
  | "review-brief"
  | "add-to-session"
  | "ask-author"
  | "check-reply"
  | "fix-yourself"
  | "waiting-on-author"
  | "ready-for-sprint";

/** The Next step column's words; a person who cannot take an action reads the same words as text. */
export const NEXT_STEP_WORDS: Record<NextStep, string> = {
  "review-brief": "Review brief",
  "add-to-session": "Add to session",
  "ask-author": "Ask the author",
  "check-reply": "Check the reply",
  "fix-yourself": "Fix it yourself",
  "waiting-on-author": "Waiting on the author",
  "ready-for-sprint": "Ready for a sprint",
};

/** Confidence in words, never the raw enum. */
export function confidenceWords(confidence: string): string {
  switch (confidence) {
    case "high":
      return "Quite sure";
    case "medium":
      return "Fairly sure";
    case "low":
      return "Not sure yet";
    default:
      return "Not sure yet";
  }
}

/** Chip tone of a confidence level. */
export function confidenceTone(confidence: string): "ok" | "warn" | "none" {
  if (confidence === "high") return "ok";
  if (confidence === "medium") return "warn";
  return "none";
}

/**
 * The brief states (01-roles.md §2 glossary): Held, Posted, Held back and
 * Out of date stay the names in docs and code. The screens show the words in
 * `BRIEF_STATE_WORDS` (01-roles.md §9.6, 2026-10-20), which say whether the
 * brief is in ADO. Every screen takes them from here, never retyped (UX-PM-09).
 */
export type BriefState = "not-posted" | "posted" | "held-back" | "out-of-date";

export const BRIEF_STATE_WORDS: Record<BriefState, string> = {
  "not-posted": "Brief not posted",
  posted: "Brief posted",
  "held-back": "Brief held back",
  "out-of-date": "Brief out of date",
};

/** Chip tone of each brief state: blue until posted, green once in ADO, grey held back, amber out of date. */
export const BRIEF_STATE_TONES: Record<BriefState, "cur" | "ok" | "none" | "warn"> = {
  "not-posted": "cur",
  posted: "ok",
  "held-back": "none",
  "out-of-date": "warn",
};

/** Comment statuses that mean the brief is in ADO. */
export const POSTED_COMMENT_STATUSES: ReadonlySet<string> = new Set(["posted", "updated", "reposted"]);

/**
 * The brief state from the code values: `commentStatus`
 * (held/posted/updated/reposted/failed/skipped/pending) and, inside a run,
 * `reviewState` (pending/posted/skipped). Out of date wins over everything,
 * then held back, then posted; anything else (held, failed, pending) is not
 * posted yet.
 */
export function briefStateOf(input: {
  commentStatus: string;
  stale?: boolean;
  reviewState?: string | null;
}): BriefState {
  if (input.stale) return "out-of-date";
  if (input.commentStatus === "skipped" || input.reviewState === "skipped") return "held-back";
  if (POSTED_COMMENT_STATUSES.has(input.commentStatus) || input.reviewState === "posted") return "posted";
  return "not-posted";
}

/**
 * The brief state of a batch item that has a brief (status `briefed` or
 * `current`), or null while it is unknown. Batch reads carry no staleness,
 * and a `current` item carries no brief at all (the run's start check found
 * its latest brief still current and linked none), so `latest` is the item's
 * latest brief from the backlog read (`aiSuggestion`). Its staleness counts
 * only when it is the same brief as the batch item's: a snapshot read before
 * a re-run never marks the new brief out of date. A `briefed` item whose
 * summary has not arrived yet holds the run's own new brief, which is held
 * until someone posts it. Pure.
 */
export function batchItemBriefState(
  item: { status: string; brief: { id: string; commentStatus: string } | null; reviewState: string | null },
  latest: { briefId: string; commentStatus: string; stale: boolean } | null
): BriefState | null {
  if (item.brief) {
    const stale = latest?.briefId === item.brief.id && latest.stale;
    return briefStateOf({ commentStatus: item.brief.commentStatus, stale, reviewState: item.reviewState });
  }
  if (item.status !== "current") return briefStateOf({ commentStatus: "held", reviewState: item.reviewState });
  if (!latest) return null;
  return briefStateOf({ commentStatus: latest.commentStatus, stale: latest.stale, reviewState: item.reviewState });
}

/**
 * A batch item with a brief whose state is not known yet (the backlog read
 * that carries it is not at hand): a neutral tag that claims nothing about
 * ADO, never "Brief not posted".
 */
export const HAS_BRIEF_TEXT = "Has a brief";

/** A brief state's tag: the shared words and tone, or the neutral {@link HAS_BRIEF_TEXT} while it is unknown. */
export function briefStateTag(state: BriefState | null): { text: string; tone: "cur" | "ok" | "none" | "warn" } {
  return state ? { text: BRIEF_STATE_WORDS[state], tone: BRIEF_STATE_TONES[state] } : { text: HAS_BRIEF_TEXT, tone: "none" };
}

/**
 * A run that finished a short while ago, on its queue badge: neutral, since
 * the brief may have been posted or held back since (UX-PM-09).
 */
export const FINISHED_TEXT = "Finished";

/**
 * A batch item the run left out (batch status `skipped`). Never "Skipped" or
 * "Held back": held back is a review state (UX-PM-09).
 */
export const LEFT_OUT_TEXT = "Left out of this run";

/** The heading of the list of items to fix before the next grooming meeting. */
export const FIX_LIST_HEADING = "Fix before the next meeting";

/** Lower-case the first letter, for state lines such as "Ready to groom · brief not posted". */
export function lowerFirst(text: string): string {
  return text.length === 0 ? text : text.charAt(0).toLowerCase() + text.slice(1);
}

/** A size in words: "Small", "Medium", "Large" or "Very large". */
export function sizeWord(points: number): string {
  if (points <= 2) return "Small";
  if (points <= 5) return "Medium";
  if (points <= 8) return "Large";
  return "Very large";
}

/** "Medium, about 5 points." */
export function sizeSentence(points: number): string {
  return `${sizeWord(points)}, about ${String(points)} ${points === 1 ? "point" : "points"}.`;
}

/** "Could be 3 to 8." or "" when the range is a single value. */
export function rangeSentence(low: number, high: number): string {
  return low === high ? "" : `Could be ${String(low)} to ${String(high)}.`;
}

/** "About 1.5 sprints at the team's recent pace", or the reason there is no figure. */
export function runwaySentence(sprints: number | null): string {
  if (sprints === null) return "The team has no finished sprints to measure its pace yet";
  return `About ${String(sprints)} ${sprints === 1 ? "sprint" : "sprints"} at the team's recent pace`;
}

/** The single-item grooming command for an item, as an in-session slash command. */
export function groomCommand(itemId: string): string {
  return `/spectree:ado-groom ${itemId}`;
}

/** Quote a project name for a slash command when it holds a space. */
export function quoteArg(value: string): string {
  return /\s/.test(value) ? `"${value.replace(/"/g, '\\"')}"` : value;
}
